// @ts-nocheck
import React from 'react';
import { sx } from '../sx';
import CohortGuardsSettings from './CohortGuardsSettings';
import CohortRuleFields from './CohortRuleFields';
import InfoTip from './InfoTip';

const CARD = `background:#fff;border:1px solid #DCE4F2;border-radius:24px;padding:22px 24px;scroll-margin-top:76px`;
const KICKER = `font-size:11px;font-weight:500;letter-spacing:0.14em;text-transform:uppercase;color:var(--ink-4)`;
const LABEL = `font-size:11px;font-weight:500;letter-spacing:0.08em;text-transform:uppercase;color:var(--ink-4)`;
const PILL = `flex:none;height:24px;padding:0 10px;border:1px solid #DCE4F2;border-radius:999px;background:#fff;color:var(--ink-4);font-family:inherit;font-size:11px;cursor:pointer`;

const jumpTo = id => {
  const el = document.getElementById('gm-settings-' + id);
  if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

function CardHead({ kicker, title, info, right }) {
  return (
    <>
      <div style={sx(`display:flex;align-items:center;gap:10px;flex-wrap:wrap`)}>
        <div style={sx(KICKER)}>{kicker}</div>
        <span style={sx(`flex:1`)}></span>
        {right}
      </div>
      <div style={sx(`display:flex;align-items:center;gap:8px;margin:6px 0 14px`)}>
        <h4 style={sx(`margin:0`)}>{title}</h4>
        <InfoTip text={info} label={title} />
      </div>
    </>
  );
}

export default function AuthoringScreen({ vm }: { vm: any }) {
  if (!vm.showAuthoring) return null;
  const sec = id => (vm.settingsSection && vm.settingsSection[id]) || { visible: true, info: '' };
  const client = vm.settingsClient || { fy: true, ccy: true };
  const showClient = sec('client').visible;

  return (
    <div style={sx(`flex:1;min-height:0;overflow:auto;padding:0 24px 24px`)}>
      <div style={sx(`display:flex;flex-direction:column;gap:16px;width:100%`)}>
        {vm.settingsSections ? (
          <div role="search" style={sx(`position:sticky;top:0;z-index:5;display:flex;align-items:center;gap:10px;flex-wrap:wrap;padding:16px 0 12px;background:var(--bg-1);border-bottom:1px solid #DCE4F2`)}>
            <div style={sx(`flex:1 1 300px;max-width:420px;display:flex;align-items:center;gap:8px;height:34px;padding:0 6px 0 12px;border:1px solid #C8D4EA;border-radius:999px;background:#fff`)}>
              <span aria-hidden="true" style={sx(`flex:none;font-size:14px;color:var(--ink-4)`)}>⌕</span>
              <input
                type="search"
                aria-label="Search settings"
                placeholder="Search drivers, cohort input, currency…"
                value={vm.settingsQuery}
                onChange={vm.onSettingsQuery}
                onKeyDown={vm.onSettingsQueryKey}
                style={sx(`flex:1;min-width:0;height:30px;border:none;background:none;font-family:inherit;font-size:12.5px;color:var(--ink);outline:none`)}
              />
              {vm.settingsFiltering ? (
                <button aria-label="Clear search" onClick={vm.clearSettingsQuery} style={sx(`flex:none;width:22px;height:22px;border:none;border-radius:50%;background:#EDF1F8;color:var(--ink-3);font-family:inherit;font-size:13px;line-height:1;cursor:pointer`)}>×</button>
              ) : null}
            </div>
            <nav aria-label="Settings sections" style={sx(`display:flex;gap:6px;flex-wrap:wrap`)}>
              {vm.settingsSections.map(s => (
                <button
                  key={s.id}
                  className="gmHover"
                  onClick={() => jumpTo(s.id)}
                  disabled={!s.visible}
                  style={sx(`flex:none;display:flex;align-items:center;gap:6px;height:28px;padding:0 11px;border:1px solid ${s.chipBorder};border-radius:999px;background:${s.chipBg};color:${s.chipFg};font-family:inherit;font-size:12px;cursor:${s.visible ? 'pointer' : 'default'}`)}
                  data-hover="border-color:#0052CD"
                >
                  {s.chip}
                  {s.badge ? <span style={sx(`font-size:10.5px;font-weight:600;font-variant-numeric:tabular-nums`)}>{s.badge}</span> : null}
                </button>
              ))}
            </nav>
          </div>
        ) : (
          <div style={sx(`height:8px`)}></div>
        )}

        {vm.settingsNoMatch ? (
          <div style={sx(`${CARD};display:flex;align-items:center;gap:12px`)}>
            <span style={sx(`flex:1;font-size:12.5px;color:var(--ink-3)`)}>{vm.settingsNoMatchNote}</span>
            <button className="gmHover" onClick={vm.clearSettingsQuery} style={sx(PILL)} data-hover="border-color:#0052CD;color:#0052CD">Show all settings</button>
          </div>
        ) : null}

        {sec('params').visible ? (
          <div id="gm-settings-params" style={sx(CARD)}>
            <CardHead kicker="Model settings" title="Versioned with the revision" info={sec('params').info} />
            {(vm.modelParams || []).map((p, pIdx) => (
              <div key={pIdx} style={sx(`display:flex;align-items:center;justify-content:space-between;gap:12px;padding:11px 0;border-top:1px solid #EDF1F8`)}>
                <span style={sx(`display:flex;align-items:center;gap:6px;min-width:0`)}>
                  <span style={sx(`font-size:12px;color:var(--ink-3);word-break:break-all`)}>{p.key}</span>
                  <InfoTip text={p.note} label={p.key} />
                </span>
                {p.isRead ? (
                  <span style={sx(`font-size:12.5px;color:var(--ink);text-align:right;font-variant-numeric:tabular-nums`)}>{p.value}</span>
                ) : null}
                {p.isEdit && p.edit === 'select' ? (
                  <select className="gmFocus" value={p.value} onChange={p.onChange} style={sx(`flex:none;max-width:340px;height:30px;padding:0 8px;border:1px solid #C8D4EA;border-radius:8px;font-family:inherit;font-size:12.5px;color:var(--ink);background:#fff;outline:none;cursor:pointer`)}>
                    {(p.options || []).map((o, oIdx) => (
                      <option key={oIdx} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                ) : null}
                {p.isEdit && (p.edit === 'date' || p.edit === 'text') ? (
                  <input className="gmFocus" type={p.edit === 'date' ? 'date' : 'text'} value={p.value || ''} onChange={p.onChange} placeholder={p.placeholder || ''} style={sx(`flex:none;max-width:340px;height:30px;padding:0 8px;border:1px solid #C8D4EA;border-radius:8px;font-family:inherit;font-size:12.5px;color:var(--ink);background:#fff;outline:none`)} />
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        {(vm.cohortInputGroups || []).length && sec('cohort').visible ? (
          <div id="gm-settings-cohort" data-testid="settings-cohort-input" style={sx(CARD)}>
            <CardHead kicker="Cohort input" title="Forecast repurchase method" info={sec('cohort').info || vm.cohortInputNote} />
            {vm.cohortInputGroups.map(g => (
              <React.Fragment key={g.label}>
                {g.blocks.map(b => (
                  <div key={b.id} role="group" aria-label={g.label + ' ' + b.label + ' cohort input'} style={sx(`padding:11px 0;border-top:1px solid #EDF1F8`)}>
                    <div style={sx(`display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap`)}>
                      <span style={sx(`display:flex;align-items:center;gap:6px`)}>
                        <span style={sx(`font-size:12.5px;color:var(--ink)`)}>{g.label} · {b.label}</span>
                        <InfoTip text={b.fields.summary} label={g.label + ' ' + b.label + ' method'} />
                      </span>
                      <div style={sx(`flex:none;display:flex;align-items:center;gap:6px`)}>
                        <CohortRuleFields f={b.fields} />
                        {b.fields.canReset ? (
                          <button className="gmHover" onClick={b.fields.onReset} style={sx(`flex:none;white-space:nowrap;height:28px;padding:0 10px;border:1px solid #C8D4EA;border-radius:999px;background:#fff;color:#0060a8;font-family:inherit;font-size:12px;cursor:pointer`)} data-hover="border-color:#00B3FF">{b.fields.resetLabel}</button>
                        ) : null}
                      </div>
                    </div>
                    {b.rows.map(r => (
                      <div key={r.label} style={sx(`display:flex;align-items:center;gap:8px;margin-top:6px;padding:5px 10px;border-radius:8px;background:var(--bg-2);font-size:11.5px;color:var(--ink-2)`)}>
                        <span style={sx(`flex:none;width:56px;color:var(--ink)`)}>{r.label}</span>
                        <span style={sx(`flex:1;min-width:0`)}>{r.summary}</span>
                        {r.onRemove ? (
                          <button className="gmHover" aria-label={'remove ' + r.label + ' rule'} onClick={r.onRemove} style={sx(`flex:none;height:22px;padding:0 9px;border:1px solid #DCE4F2;border-radius:999px;background:#fff;color:var(--ink-4);font-family:inherit;font-size:11px;cursor:pointer`)} data-hover="border-color:#0052CD;color:#0052CD">Use default</button>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        ) : null}

        {vm.cohortGuardsView && sec('guards').visible ? (
          <div id="gm-settings-guards" data-testid="settings-cohort-guards" style={sx(CARD)}>
            <CardHead kicker="Cohort model settings" title="Forecast guardrails" info={sec('guards').info || vm.cohortGuardsView.info} />
            <CohortGuardsSettings view={vm.cohortGuardsView} />
          </div>
        ) : null}

        {showClient ? (
          <div id="gm-settings-client" style={sx(CARD)}>
            <CardHead kicker="Client settings" title={vm.clientSettingsTitle || 'Client'} info={sec('client').info} />
            {client.fy ? (
              <div>
                <div style={sx(`display:flex;align-items:center;gap:6px;margin-bottom:8px`)}>
                  <span style={sx(LABEL)}>financial_year_start_month</span>
                  <InfoTip text={vm.fyNote} label="financial_year_start_month" />
                </div>
                <div style={sx(`display:flex;gap:5px;flex-wrap:wrap`)}>
                  {(vm.fyMonths || []).map((m, mIdx) => (
                    <button key={mIdx} onClick={m.onClick} style={sx(`flex:none;width:44px;height:28px;border:1px solid ${m.border};border-radius:8px;background:${m.bg};color:${m.fg};font-family:inherit;font-size:11.5px;font-weight:${m.fw};cursor:pointer`)}>{m.label}</button>
                  ))}
                </div>
              </div>
            ) : null}
            {client.ccy ? (
              <div style={sx(client.fy ? `margin-top:16px;padding-top:16px;border-top:1px solid #EDF1F8` : ``)}>
                <div style={sx(`display:flex;align-items:center;gap:6px;margin-bottom:8px`)}>
                  <span style={sx(LABEL)}>reporting_currency</span>
                  <InfoTip text={vm.ccyNote} label="reporting_currency" />
                </div>
                <div style={sx(`display:flex;gap:5px;flex-wrap:wrap`)}>
                  {(vm.ccyOpts || []).map((c, cIdx) => (
                    <button key={cIdx} onClick={c.onClick} style={sx(`flex:none;height:28px;padding:0 11px;border:1px solid ${c.border};border-radius:8px;background:${c.bg};color:${c.fg};font-family:inherit;font-size:11.5px;font-weight:${c.fw};cursor:pointer`)}>{c.label}</button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
) : null} 
      </div>
    </div>
  );
}
